import { motion } from "motion/react";
import { Activity, MessageSquare, GitFork, Scale, FileText, Users, TrendingUp, Brain } from "lucide-react";
import type { StrainGraph } from "@/types";
import { computeCruxes, stanceSummary } from "@/lib/graph";
import { StanceSlider } from "@/components/StanceSlider";
import { edgeMeta } from "@/lib/vocabulary";
import { cn } from "@/lib/cn";

export function StatsView({ graph }: { graph: StrainGraph }) {
  const cruxes = computeCruxes(graph);
  const stance = stanceSummary(graph);

  const totalComments = graph.nodes.reduce((a, n) => a + (n.stats?.comments ?? 0), 0);
  const totalForks = graph.nodes.reduce((a, n) => a + (n.stats?.forks ?? 0), 0);
  const totalEvidence = graph.nodes.reduce((a, n) => a + (n.stats?.evidence ?? 0), 0);
  const totalMindChanges = graph.nodes.reduce((a, n) => a + (n.stats?.mind_changes ?? 0), 0);

  // edge-type distribution
  const edgeDist = graph.edges.reduce((acc, e) => {
    acc[e.relation] = (acc[e.relation] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // node-type distribution
  const nodeDist = graph.nodes.reduce((acc, n) => {
    acc[n.type] = (acc[n.type] ?? 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="h-full overflow-y-auto bg-grid-fine">
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-1">stats</div>
          <h2 className="text-lg font-semibold">The shape of this strain</h2>
          <p className="text-[13px] text-[var(--color-ink-muted)] mt-1">
            Not a leaderboard — a snapshot of where attention, disagreement, and belief-change live.
          </p>
        </div>

        {/* big metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <BigMetric icon={<MessageSquare size={16} />} value={totalComments} label="comments" tone="primary" />
          <BigMetric icon={<GitFork size={16} />} value={totalForks} label="forks" tone="inferred" />
          <BigMetric icon={<FileText size={16} />} value={totalEvidence} label="evidence" tone="info" />
          <BigMetric icon={<Brain size={16} />} value={totalMindChanges} label="minds moved" tone="amber" />
        </div>

        {/* edge distribution */}
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-3">relation types</div>
          <div className="space-y-2">
            {Object.entries(edgeDist).sort((a, b) => b[1] - a[1]).map(([rel, count]) => {
              const meta = edgeMeta(rel as any);
              const pct = (count / graph.edges.length) * 100;
              return (
                <div key={rel} className="flex items-center gap-3">
                  <span className="text-[12px] text-[var(--color-ink-soft)] w-24">{meta.label}</span>
                  <div className="flex-1 h-2 rounded-full bg-[var(--color-surface-3)] overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ background: meta.color }}
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    />
                  </div>
                  <span className="text-[11px] font-mono text-[var(--color-ink-muted)] w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* crux leaderboard */}
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-3">
            <Scale size={12} /> top cruxes
          </div>
          <div className="space-y-2">
            {cruxes.slice(0, 5).map((c, i) => (
              <div key={c.node_id} className="flex items-center gap-2">
                <span className="font-mono text-[11px] text-[var(--color-ink-faint)] w-4">{i + 1}</span>
                <span className="text-[12px] text-[var(--color-ink-soft)] flex-1 truncate">{c.node_text}</span>
                <div className="w-20 h-1.5 rounded-full bg-[var(--color-surface-3)] overflow-hidden">
                  <div className="h-full bg-[var(--color-secondary)]" style={{ width: `${Math.min(100, (c.score / cruxes[0].score) * 100)}%` }} />
                </div>
                <span className="font-mono text-[11px] text-[var(--color-secondary)] w-6 text-right">{c.score}</span>
              </div>
            ))}
          </div>
        </div>

        {/* stance slider */}
        <StanceSlider strainId={graph.strain.id} versionId={graph.version.id} />

        {/* belief-change summary */}
        <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
          <div className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[var(--color-ink-muted)] mb-3">
            <Activity size={12} /> belief change so far
          </div>
          <div className="flex items-center gap-4">
            <div className="text-center">
              <div className="text-[10px] font-mono text-[var(--color-ink-faint)] uppercase tracking-wider">avg before</div>
              <div className="text-2xl font-semibold font-mono text-[var(--color-ink-muted)]">{stance.before}</div>
            </div>
            <div className="flex-1 flex items-center">
              <div className="h-1 flex-1 rounded-full bg-gradient-to-r from-[var(--color-rebut)] via-[var(--color-ink-faint)] to-[var(--color-support)] opacity-40" />
            </div>
            <div className="text-center">
              <div className="text-[10px] font-mono text-[var(--color-ink-faint)] uppercase tracking-wider">avg after</div>
              <div className="text-2xl font-semibold font-mono text-[var(--color-primary)]">{stance.after}</div>
            </div>
            <div className="text-center ml-2">
              <div className="text-[10px] font-mono text-[var(--color-ink-faint)] uppercase tracking-wider">moved</div>
              <div className={cn("text-2xl font-semibold font-mono", stance.moved > 0 ? "text-[var(--color-support)]" : stance.moved < 0 ? "text-[var(--color-rebut)]" : "text-[var(--color-ink-muted)]")}>
                {stance.moved > 0 ? "+" : ""}{stance.moved}
              </div>
            </div>
          </div>
          <p className="text-[11px] text-[var(--color-ink-faint)] mt-3">
            Aggregated from node agreement and stance surveys. No winners — just where the room moved.
          </p>
        </div>
      </div>
    </div>
  );
}

function BigMetric({ icon, value, label, tone }: { icon: React.ReactNode; value: number; label: string; tone: string }) {
  const color = tone === "primary" ? "var(--color-primary)"
    : tone === "inferred" ? "var(--color-inferred)"
    : tone === "amber" ? "var(--color-secondary)"
    : "var(--color-define)";
  return (
    <div className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div style={{ color }}>{icon}</div>
      <div className="text-2xl font-semibold font-mono mt-2 text-[var(--color-ink)]">{value}</div>
      <div className="text-[11px] text-[var(--color-ink-muted)]">{label}</div>
    </div>
  );
}
